import { ISelectOption, selectOption, selectOptionsFrom } from '@/platform/controls/i-select-option';

import {
	ApplicationBasis,
	Degree,
	Gender,
	Race,
	ScholarshipFormat,
	StudyExperience,
	ScholarshipType,
} from '@studyportals/sp-scholarships-api-interface';

import CountryPresenter from '@/presenters/country-presenter';
import DisciplinePresenter from '@/presenters/discipline-presenter';
import CurrencyPresenter from '@/presenters/currency-presenter';
import { SelectableData, selectableDataFromItems } from './csv-input/selectable-data';

export class ScholarshipRelatedData {
	public destinationCountries: readonly SelectableData[] = [];
	public destinationStates: readonly SelectableData[] = [];
	public originCountries: readonly SelectableData[] = [];
	public originStates: readonly SelectableData[] = [];
	public disciplines: readonly SelectableData[] = [];
	public currencies: readonly ISelectOption<string>[] = [];

	public readonly degrees: readonly ISelectOption<Degree>[];
	public readonly applicationBasis: readonly ISelectOption<ApplicationBasis>[];
	public readonly gender: readonly ISelectOption<Gender>[];
	public readonly studyExperience: readonly ISelectOption<StudyExperience>[];
	public readonly formats: readonly ISelectOption<ScholarshipFormat>[];
	public readonly types: readonly ISelectOption<ScholarshipType>[];
	public readonly race: readonly ISelectOption<Race>[];

	public constructor() {
		this.degrees = [
			selectOption(Degree.BACHELOR, 'Bachelor'),
			selectOption(Degree.MASTER, 'Master'),
			selectOption(Degree.PHD, 'PhD'),
		];

		this.applicationBasis = [
			selectOption(ApplicationBasis.MERIT, 'Merit'),
			selectOption(ApplicationBasis.NEED, 'Need'),
		];

		this.gender = [
			selectOption(Gender.MALE, 'Male'),
			selectOption(Gender.FEMALE, 'Female'),
			selectOption(Gender.OTHER, 'Other'),
		];

		this.studyExperience = [
			selectOption(StudyExperience.NONE, 'None'),
			selectOption(StudyExperience.HIGH_SCHOOL, 'High school'),
			selectOption(StudyExperience.BSC, 'Bachelor'),
			selectOption(StudyExperience.MSC, 'Master'),
			selectOption(StudyExperience.PHD, 'PhD'),
		];

		this.formats = [
			selectOption(ScholarshipFormat.TUITION_FEE_REDUCTION, 'Tuition fee reduction'),
			selectOption(ScholarshipFormat.LIVING_EXPENSES, 'Living expenses'),
			selectOption(ScholarshipFormat.ACCOMMODATION, 'Accommodation'),
			selectOption(ScholarshipFormat.TRAVEL_EXPENSES, 'Travel expenses'),
			selectOption(ScholarshipFormat.SPOUSE_CHILD_CARE, 'Spouse child care'),
			selectOption(ScholarshipFormat.HEALTH, 'Health'),
			selectOption(ScholarshipFormat.APPLICATION, 'Application'),
			selectOption(ScholarshipFormat.OTHER, 'Other'),
		];

		this.race = [
			selectOption(Race.ASIAN, 'Asian'),
			selectOption(Race.BLACK, 'Black'),
			selectOption(Race.HISPANIC, 'Hispanic'),
			selectOption(Race.MIXED, 'Mixed'),
			selectOption(Race.WHITE, 'White'),
			selectOption(Race.OTHER, 'Other'),
		];

		this.types = [
			selectOption(ScholarshipType.FULL_TUITION_FEE_WAIVER, 'Full tuition fee waiver'),
			selectOption(ScholarshipType.PARTIAL_FEE_WAIVER, 'Partial fee waiver'),
			selectOption(ScholarshipType.FULL_RIDE, 'Full ride'),
			selectOption(ScholarshipType.MILITARY, 'Military'),
			selectOption(ScholarshipType.SPORTS, 'Sports'),
			selectOption(ScholarshipType.STEM, 'STEM'),
			selectOption(ScholarshipType.RELIGIOUS, 'Religious'),
			selectOption(ScholarshipType.HERITAGE, 'Heritage'),
			selectOption(ScholarshipType.LEARNING_DISABILITIES, 'Learning disabilities'),
			selectOption(ScholarshipType.AUTOMATIC, 'Automatic'),
			selectOption(ScholarshipType.MERIT_BASED, 'Merit-based'),
			selectOption(ScholarshipType.NEED_BASED, 'Need-based'),
			selectOption(ScholarshipType.NO_ESSAY_REQUIRED, 'No essay required'),
			selectOption(ScholarshipType.NO_GPA_REQUIREMENTS, 'No GPA requirements'),
			selectOption(ScholarshipType.NO_RECOMMENDATIONS_REQUIRED, 'No recommendations required'),
		];
	}

	public static async build(target: ScholarshipRelatedData): Promise<void> {
		target.destinationCountries = selectableDataFromItems(
			await CountryPresenter.all(),
			(c) => c.id,
			(c) => c.name
		);
		target.originCountries = selectableDataFromItems(
			await CountryPresenter.all(),
			(c) => c.id,
			(c) => c.name
		);
		target.disciplines = selectableDataFromItems(
			await DisciplinePresenter.all(),
			(d) => d.id,
			(d) => d.name
		);
		target.currencies = selectOptionsFrom(
			await CurrencyPresenter.all(),
			(c) => c.isoCode,
			(c) => c.name
		);
	}
}
